import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  SESSION_EXPIRED_EVENT,
  dispatchSessionExpired,
  getProactiveRefreshIntervalMs,
  refreshSessionWithQueue,
} from '../../api/sessionRefresh';
import { isPublicRoute } from '../../utils/authRouteUtils';

const SessionRefreshManager = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const lastRefreshTimeRef = useRef(Date.now());

  useEffect(() => {
    const handleSessionExpired = async () => {
      const currentPath = window.location.pathname;
      if (isPublicRoute(currentPath)) {
        return;
      }

      try {
        await logout();
      } catch {
        // ignore
      }

      if (currentPath !== '/login' && !currentPath.includes('/login')) {
        navigate('/login', { replace: true, state: { sessionExpired: true } });
      }
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    lastRefreshTimeRef.current = Date.now();
    const refreshIntervalMs = getProactiveRefreshIntervalMs();

    const refreshSilently = async () => {
      try {
        await refreshSessionWithQueue();
        lastRefreshTimeRef.current = Date.now();
      } catch (error) {
        if (error?.response?.status === 401) {
          dispatchSessionExpired();
        }
      }
    };

    const intervalId = setInterval(refreshSilently, refreshIntervalMs);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastRefresh = Date.now() - lastRefreshTimeRef.current;
        if (timeSinceLastRefresh >= refreshIntervalMs) {
          refreshSilently();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  return null;
};

export default SessionRefreshManager;

